// src/components/sourcex/QuestionBuilder.jsx
//
// SourceX template editor — replaces the old "type a label, pick ONE type
// from a dropdown, then hand-type Table columns as Label:Type,Label:Type"
// inline edit form.
//
// A question starts as just a text box. The coordinator then drags (or
// clicks) answer types in from FieldTypePalette:
//   - exactly one non-Table piece  -> an ordinary single-type question
//   - two or more pieces           -> a COMPOSITE question: several answer
//     pieces bundled under one question, stored as a real Table with one row
//     and no row labels (see isCompositeTable in templateSchema.js) so every
//     existing renderer/exporter already knows how to show it
//   - Table                        -> its own thing (a repeatable log). It
//     asks how many rows and columns first (TableSizeModal), then hands the
//     coordinator the actual grid to build on (TableGridEditor) — Word's
//     model, not a stack of column cards. Every setting (columns, row mode,
//     row labels, how many rows print, starting values) stays editable
//     afterward instead of being a one-time upfront choice
//

// This builder carries no preview of its own. Every question in the document
// already shows its printed form underneath it (SourcePreviewField), and the
// header's Preview button shows the whole visit on a real page — so a third
// copy inside the editor only added scrolling between the question and the
// Save button.
//
// Conditional visibility lives behind the "Make conditional" button next to
// Required, not open at the foot of the form: most questions never branch, and
// the ones that do only need the rule builder while it's being set. Every
// change in that modal persists immediately (onVisibilityChange), exactly as it
// did when the builder sat open — closing it is not a save step.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ChevronUp,
  ChevronDown,
  Plus,
  Table2,
  Sparkles,
  Check,
  GitBranch,
  AlertTriangle,
  LibraryBig,
} from "lucide-react";
import { inputCls, selectCls } from "./QuestionField";
import OptionsEditor, { parseOptionList } from "./OptionsEditor";
import ConditionBuilder from "./ConditionBuilder";
import DefaultValueInput, {
  PREFILL_HINT,
  PREFILL_LABEL,
  prefillChipCls,
} from "./DefaultValueInput";
import TableGridEditor, { blankColumn, tableRowCount } from "./TableGridEditor";
import TableSizeModal from "./TableSizeModal";
import {
  parseCondition,
  buildCondition,
  describeCondition,
} from "../../services/templateSchema";
import {
  isFieldTypeDrag,
  readFieldTypeDrag,
  PALETTE_TYPES,
  FIELD_TYPE_LABELS as PIECE_TYPE_LABELS,
  FIELD_TYPE_HINTS,
} from "./FieldTypePalette";
import { isTemplateDrag, readTemplateId } from "../../services/templateDrag";

// Same names the palette uses (imported as PIECE_TYPE_LABELS so the rest of
// this file didn't need renaming) — Table and Header are handled specially
// elsewhere, not as a generic piece.
// PALETTE_TYPES, so the chips here and the rail on the left offer the same
// types in the same order — Dropdown and Multi Select adjacent, in particular.
const PIECE_TYPES = PALETTE_TYPES.filter((t) => t !== "Table");

// The one colour a pre-fill control ever wears. It is the only OPTIONAL,
// example-ish field in a card that is otherwise all structure, and wearing the
// same blue in every answer card is what lets it be recognised at a glance
// instead of read. Blue rather than the old near-grey cyan so the control
// announces itself — nobody uses a feature they never notice.
// No width in here on purpose. It used to start with `w-full`, which collided
// with the `w-28` on the Number unit box beside it — two width utilities of
// equal specificity, so which won came down to their order in Tailwind's
// output, not the order they're written. `w-full` won, the box was told to be
// 100% wide, and `shrink-0` stopped it shrinking back: it ran straight out of
// the answer card. Each use states its own width now.
const prefillCls =
  "px-3 py-2 rounded-xl bg-blue-500/[0.12] border border-blue-400/45 text-sm text-slate-100 placeholder:text-blue-200/50 focus:outline-none focus:border-blue-300/80 focus:bg-blue-500/[0.18] focus:ring-2 focus:ring-blue-500/30 transition [color-scheme:dark]";

let pieceSeq = 0;
const newPieceId = () => `piece_${Date.now().toString(36)}_${(pieceSeq++).toString(36)}`;

const blankPiece = (type) => ({
  key: newPieceId(),
  type,
  label: "",
  unit: "",
  options: type === "Select" || type === "MultiSelect" ? [] : undefined,
});

// A fresh table's starting shape, at the size just picked in TableSizeModal.
// That size is only a STARTING point: rows and columns are inserted, appended
// and removed on the grid itself, and every other setting (row mode, row
// labels, starting values) stays editable the whole time — so nothing chosen
// in the modal is a decision that has to be got right first time.
/**
 * The table draft → the makeQuestion()-ready shape it saves as. One function
 * for both the Save button and the live preview, so "exactly how this prints"
 * is a promise about the same object that gets persisted.
 *
 * Columns keep their builder key as their real id (rather than letting
 * makeQuestion mint a fresh one) so prefillRows — keyed by that same id —
 * still points at the right column afterwards.
 *
 * Two row shapes come out of here, and which one depends purely on whether
 * this table carries legacy `rowLabels`:
 *   - with labels (a table built before the grid editor): the label list IS
 *     the row count, so minRows stays 0, exactly as those tables were saved.
 *   - without (everything new): minRows is the row count, and the row count
 *     alone decides how many rows print. No leading label column either way.
 * `prefillRows` is written for both — a starting value belongs to a row slot,
 * and every row here is a slot whether or not it happens to have a name.
 */
// PRINT LAYOUT THIS EDITOR DOES NOT EDIT, AND THEREFORE MUST NOT DESTROY.
//
// A table built from a real paper form carries measurements this builder has no
// controls for: the column shares taken off the source PDF, the writing space
// per row, the "No." lead column, the sub-headings inside an eligibility
// checklist. `columns` and the row fields are FACTORY_OWNED (see
// templateSchema.applyQuestionEdit), so a payload that omits them doesn't leave
// them alone — it DELETES them. Opening a standing log's grid and fixing a
// typo used to flatten its measured columns to equal shares and un-space its
// rows; opening a checklist's would drop every "Medical Conditions:" heading in
// it. They ride through the draft untouched instead.
const PRINT_COLUMN_FIELDS = [
  "width",
  "sublabel",
  "group",
  "stack",
  "structural",
  "emphasize",
];
const PRINT_TABLE_FIELDS = [
  "rowLabelWidthPct",
  "rowHeightPt",
  "headerFontPt",
  "numbered",
  "numberHeader",
  "groupRows",
];

const pickPrint = (src, fields) => {
  const out = {};
  fields.forEach((f) => {
    if (src && src[f] !== undefined) out[f] = src[f];
  });
  return out;
};

const tablePayload = (table, question, required) => {
  const rows = tableRowCount(table);
  const hasRowLabels = (table.rowLabels || []).length > 0;
  return {
    question,
    required,
    type: "Table",
    ...(table.print || {}),
    columns: table.columns.map((c) => ({
      id: c.key,
      label: c.label,
      type: c.type,
      unit: c.unit,
      options: c.options,
      ...(c.print || {}),
    })),
    rowLabels: hasRowLabels ? table.rowLabels : [],
    rowLabelHeader: hasRowLabels ? table.rowLabelHeader : "",
    minRows: hasRowLabels ? 0 : rows,
    maxRows: table.maxRows,
    prefillRows: Array.from({ length: rows }, (_, ri) => {
      const src = table.prefillRows?.[ri] || {};
      const out = {};
      // Values from a column that has since been deleted are dropped here
      // rather than persisted as orphans.
      table.columns.forEach((c) => {
        if (src[c.key]) out[c.key] = src[c.key];
      });
      return out;
    }),
  };
};

const defaultTableState = (rows, cols) => ({
  columns: Array.from({ length: cols }, (_, i) => blankColumn(i + 1)),
  // No rowLabels: a new table is exactly the rows and columns drawn on the
  // grid. A table wanting row names puts them in its own first column, so
  // nothing prints an extra leading column nobody asked for.
  rowLabels: [],
  rowLabelHeader: "",
  minRows: rows,
  maxRows: 50,
  prefillRows: [],
});

/**
 * One answer piece — its type, whatever that type needs (choices, a unit) and
 * the value it starts filled in with. Everything for one piece is in one card,
 * in the order a coordinator thinks about it.
 *
 * Two things this deliberately does NOT do any more:
 *
 *  - It doesn't ask for a "label" on a single-piece question. There, the
 *    question's own text is the label and the piece's label prints nowhere —
 *    so it was a required-looking box whose contents vanished. The label
 *    survives only for a COMPOSITE question (2+ pieces), where each piece's
 *    label really does print in front of its answer ("Weight (kg): ____"), and
 *    the card says so rather than leaving you to find out from the export.
 *
 *  - It doesn't send you to the bottom of the builder to set a default. The
 *    starting value sits directly under the answer it belongs to, which is the
 *    only place anyone was ever going to look for it.
 */
function PieceCard({
  piece,
  index,
  count,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  confirm,
  usedByOption,
  defaultValue,
  onDefaultChange,
  // Forwarded straight to OptionsEditor — see its onPendingChange.
  onPendingOptions,
}) {
  const set = (patch) => onChange({ ...piece, ...patch });
  const composite = count > 1;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        {composite && (
          <span className="shrink-0 w-5 h-5 rounded-md bg-white/[0.06] text-[10px] font-semibold text-slate-400 flex items-center justify-center">
            {index + 1}
          </span>
        )}
        <select
          className={`${selectCls} shrink-0 !py-1.5 text-xs ${composite ? "w-36" : "w-44"}`}
          value={piece.type}
          title={FIELD_TYPE_HINTS[piece.type]}
          aria-label="Answer type"
          data-tour="builder-type"
          onChange={(e) => {
            const type = e.target.value;
            set({
              type,
              options: type === "Select" || type === "MultiSelect" ? piece.options || [] : undefined,
              unit: type === "Number" ? piece.unit : "",
            });
          }}
        >
          {PIECE_TYPES.map((t) => (
            <option key={t} value={t} className="bg-[#0b0820]">
              {PIECE_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        {composite ? (
          <input
            className={`${inputCls} flex-1 min-w-0 !py-1.5 text-xs`}
            value={piece.label}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="Prints before this answer (e.g. Weight)"
          />
        ) : (
          <span className="flex-1 min-w-0 text-[11px] text-slate-500">
            {FIELD_TYPE_HINTS[piece.type]}
          </span>
        )}
        {composite && (
          <>
            <button
              type="button"
              onClick={onMoveUp}
              disabled={index === 0}
              title="Move up"
              className="shrink-0 w-6 h-7 rounded flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 disabled:opacity-30 transition"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={index === count - 1}
              title="Move down"
              className="shrink-0 w-6 h-7 rounded flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 disabled:opacity-30 transition"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onRemove}
          title="Remove this answer"
          className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/15 transition"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {composite && FIELD_TYPE_HINTS[piece.type] && (
        <p className="pl-6 text-[10px] text-slate-500">{FIELD_TYPE_HINTS[piece.type]}</p>
      )}
      {/* The starting value is set WHERE the answer is defined, never in a
          second box asking the same thing again:
            - a choice type marks it on the choice itself (the ○/☐ beside each
              row in OptionsEditor, or the fixed Yes/No chips below);
            - a write-in type gets one inline field.
          Signature is skipped entirely: it holds no answer, so a default there
          was silently dropped on save (see isSourceOnlyType). */}
      {piece.type === "Select" || piece.type === "MultiSelect" ? (
        <OptionsEditor
          type={piece.type}
          options={piece.options || []}
          onChange={(options) => set({ options })}
          confirm={confirm}
          usedBy={usedByOption}
          defaultValue={defaultValue}
          onDefaultChange={onDefaultChange}
          onPendingChange={onPendingOptions}
        />
      ) : (
        piece.type !== "Signature" && (
          // Blue, and only ever blue: the pre-fill value is the one OPTIONAL,
          // example-ish thing in a card that is otherwise all structure, and
          // the same colour marks it in every answer card so it's recognised
          // rather than read.
          // flex-wrap so a narrow card stacks the unit under the value rather
          // than pushing it past the card's edge.
          <div className="flex flex-wrap items-center gap-2">
            {/* A filled chip rather than a line of small caps: this is the one
                thing in the card a coordinator has to NOTICE before they can
                use it, and the old grey-cyan label read as a caption on the box
                beside it. `title` says what it does, since the name alone
                doesn't say the value gets printed on the blank form. */}
            <span title={PREFILL_HINT} className={prefillChipCls} data-tour="builder-prefill">
              {PREFILL_LABEL}
            </span>
            <div className="min-w-0 flex-1 basis-40">
              <DefaultValueInput
                piece={piece}
                value={defaultValue}
                onChange={onDefaultChange}
                placeholder="optional"
                tone={`w-full ${prefillCls}`}
              />
            </div>
            {/* The unit rides on the same line as the value — where a unit
                sits on any form — but NOT in the pre-fill blue. It is part of
                the field's structure (it prints whether or not anything is
                pre-filled), and painting it the same colour would make the one
                thing the blue is meant to point at point at two things. */}
            {piece.type === "Number" && (
              <input
                className={`${inputCls} shrink-0 w-24 !py-1.5 text-xs`}
                value={piece.unit}
                onChange={(e) => set({ unit: e.target.value })}
                placeholder="unit (kg)"
                title="Unit — printed after the value (optional)"
              />
            )}
          </div>
        )
      )}
    </div>
  );
}

/**
 * The "when does this question appear?" rule builder, in a centered modal.
 *
 * It used to sit permanently open at the foot of the builder, which put a
 * whole rule editor between the coordinator and the Save button on every
 * question — including the large majority that are never conditional. Same
 * builder, same live persistence; it just isn't in the way until asked for.
 */
function ConditionModal({ onClose, hasCondition, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="When does this question appear?"
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-white/12 bg-[#0c0826]/97 backdrop-blur-2xl shadow-[0_24px_70px_-20px_rgba(124,58,237,0.6)]">
        <div className="flex items-start gap-2.5 px-4 pt-4">
          <span className="shrink-0 w-8 h-8 rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/10 text-fuchsia-300 flex items-center justify-center">
            <GitBranch className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-white leading-snug">
              When does this question appear?
            </h3>
            <p className="mt-0.5 text-[11px] text-slate-400">
              {hasCondition
                ? "Change the rule, or clear it to always show this question."
                : "By default it always shows. Pick an earlier answer to hide it behind."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 pt-3">{children}</div>

        <div className="flex justify-end px-4 py-3 mt-3 border-t border-white/10 bg-white/[0.02]">
          {/* Not a save button — every change above is already persisted. */}
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-xl text-sm font-medium text-white shadow bg-gradient-to-r from-purple-600 to-blue-600 hover:brightness-110 transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// The drop target every empty/in-progress builder offers: drag a type from
// the palette here, or use the click-to-add row below it.
//
// Field types ONLY. A reusable block from "Use from template" is taken by the
// whole question card instead — see the builder's root element. Hunting for
// one small target with a block already in hand is what made that drop feel
// unreliable; a field type is different, because it adds ONE answer to a
// specific place and so has to land there.
function DropZone({ onDropType, active, children }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(ev) => {
        if (!isFieldTypeDrag(ev)) return;
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "copy";
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(ev) => {
        if (!isFieldTypeDrag(ev)) return;
        ev.preventDefault();
        setOver(false);
        const type = readFieldTypeDrag(ev);
        if (type) onDropType(type);
      }}
      // Once the question HAS its answers this wraps them rather than sitting
      // below as its own empty box, so the drop target costs no space of its
      // own — the dashed frame only appears when there is nothing here yet, or
      // while a type is actually being dragged over.
      className={`rounded-xl transition ${
        over
          ? "border-2 border-dashed border-purple-300/70 bg-purple-500/10 p-3"
          : active
          ? "border-2 border-transparent"
          : "border-2 border-dashed border-white/10 bg-white/[0.02] p-3"
      }`}
    >
      {children}
    </div>
  );
}

/**
 * @param {object} [props.initial]  an existing question being edited, or
 *   undefined/null when creating a new one
 * @param {(built: object) => void} props.onSave  called with a
 *   makeQuestion()-ready init object (never includes visibleWhen — that's
 *   persisted live via onVisibilityChange as it's edited, same as everything
 *   else in this builder)
 * @param {() => void} props.onCancel
 * @param {(option: string) => Array} [props.usedByOption]  dependency check
 *   for the OptionsEditor when this question is a single Select/MultiSelect
 *   (matches today's rename/delete-safety behaviour); not meaningful for a
 *   composite piece or table column, which nothing can branch on today
 * @param {Function} props.confirm  the shared confirm-dialog hook
 * @param {object} [props.visibleWhen]  this question's current condition, if any
 * @param {(next: object|undefined) => void} [props.onVisibilityChange]  persists
 *   a condition change immediately, same live-save pattern as the rest of the
 *   builder; the Conditional section is skipped entirely if this is omitted
 * @param {Array} [props.branchCandidates]  fields this question could be
 *   conditioned on, scoped to its section (mirrors the document's own picker)
 * @param {Array} [props.branchAllCandidates]  the wider "every field in this
 *   visit" list, offered when nothing scoped fits
 * @param {(id: string) => string} [props.sectionLabelOf]  heading label a
 *   candidate field sits under, for grouping the wider list
 * @param {string} [props.defaultBranchQuestionId]  which candidate a brand
 *   new rule should default to (the nearest preceding field with choices)
 * @param {boolean} [props.startEmpty]  `initial` is a freshly-inserted
 *   placeholder question (from "Add field"), not a real one being edited —
 *   open with NO answer piece pre-added instead of seeding one from
 *   `initial.type`, so the coordinator picks a type instead of having to
 *   delete a "Text" piece they never asked for.
 */
export default function QuestionBuilder({
  initial,
  onSave,
  onCancel,
  usedByOption,
  confirm,
  visibleWhen,
  onVisibilityChange,
  branchCandidates = [],
  branchAllCandidates = [],
  sectionLabelOf,
  defaultBranchQuestionId,
  startEmpty,
  // Optional. When given, the answer drop zone also accepts a reusable block
  // dragged out of "Use from template" — see DropZone.
  onDropTemplate,
}) {
  const editing = !!initial;
  // Independent of what's persisted, for the same reason SectionEditor's own
  // branch popover keeps a draft: buildCondition() drops a rule that doesn't
  // have a value yet (by design, so an unfinished condition never saves), so
  // re-deriving this from `visibleWhen` on every render would wipe out an
  // in-progress "picked the question, haven't picked the answer yet" step.
  const [branchDraft, setBranchDraft] = useState(() => parseCondition(visibleWhen));

  // Reconstruct the builder's starting shape from an existing question. A
  // plain single-type question becomes one piece; a composite (1-row) table
  // becomes its columns as pieces; a real repeatable table opens straight
  // into table mode. Every piece/column gets a FRESH local key regardless —
  // matching what saveEdit already did for every Table before this component
  // existed (it never passed column ids through either) — so prefill values,
  // which are keyed by column id, are remapped here by POSITION (old
  // columns[i] -> new piece[i]) rather than assumed to still match by id.
  const seed = useMemo(() => {
    if (!initial || startEmpty) {
      return { text: initial?.question || "", pieces: [], table: null, compositePrefill: {} };
    }
    const remapPrefillRow = (row, oldCols, newPieces) => {
      const out = {};
      oldCols.forEach((oldCol, i) => {
        const v = row?.[oldCol.id];
        const newKey = newPieces[i]?.key;
        if (v && newKey) out[newKey] = v;
      });
      return out;
    };
    if (initial.type === "Table") {
      const isComposite =
        initial.minRows === 1 && initial.maxRows === 1 && !(initial.rowLabels || []).length;
      const oldCols = initial.columns || [];
      if (isComposite) {
        const pieces = oldCols.map((c) => ({
          key: newPieceId(),
          type: c.type,
          label: c.label || "",
          unit: c.unit || "",
          options: c.options || [],
        }));
        return {
          text: initial.question || "",
          pieces,
          table: null,
          compositePrefill: remapPrefillRow(initial.prefillRows?.[0], oldCols, pieces),
        };
      }
      const columns = oldCols.map((c) => ({
        key: newPieceId(),
        type: c.type,
        label: c.label || "",
        unit: c.unit || "",
        options: c.options || [],
        // Carried, not edited — see PRINT_COLUMN_FIELDS.
        print: pickPrint(c, PRINT_COLUMN_FIELDS),
      }));
      const rowLabels = initial.rowLabels || [];
      // The draft always carries the row count in minRows — the grid edits
      // rows by position, so it needs one number to work from whether or not
      // this table came with a legacy label column. save() puts the count back
      // where each shape expects it.
      const rows = tableRowCount(initial);
      return {
        text: initial.question || "",
        pieces: [],
        table: {
          columns,
          rowLabels,
          rowLabelHeader: initial.rowLabelHeader || "",
          minRows: rows,
          maxRows: Number.isInteger(initial.maxRows) ? initial.maxRows : 50,
          // Every row, not just the labelled ones — a table without row labels
          // can carry starting values too.
          prefillRows: Array.from({ length: rows }, (_, ri) =>
            remapPrefillRow(initial.prefillRows?.[ri], oldCols, columns)
          ),
          // Carried, not edited — see PRINT_TABLE_FIELDS.
          print: pickPrint(initial, PRINT_TABLE_FIELDS),
        },
        compositePrefill: {},
      };
    }
    const singlePiece = {
      key: newPieceId(),
      type: initial.type || "Text",
      label: "",
      unit: initial.unit || "",
      options: initial.options || [],
    };
    return {
      text: initial.question || "",
      pieces: [singlePiece],
      table: null,
      compositePrefill:
        initial.defaultValue !== undefined
          ? { [singlePiece.key]: initial.defaultValue }
          : {},
    };
  }, [initial, startEmpty]);

  const [text, setText] = useState(seed.text);
  const [pieces, setPieces] = useState(seed.pieces);
  const [table, setTable] = useState(seed.table); // null unless in "table mode"
  const [required, setRequired] = useState(initial?.required === true);
  // Default values for a composite (multi-piece) question — one value per
  // piece, keyed by the piece's local key. Only meaningful once there are 2+
  // pieces; see the "Default values" section below.
  const [compositePrefill, setCompositePrefill] = useState(seed.compositePrefill);
  const [error, setError] = useState("");
  // True while the "how big is this table?" step is on screen. Only ever shown
  // for a table being CREATED — editing an existing one opens straight onto
  // its grid, at the size it already is.
  const [askingTableSize, setAskingTableSize] = useState(false);
  // The "when does this appear?" modal (see ConditionModal).
  const [condOpen, setCondOpen] = useState(false);
  // Whether the answer-type list is open. Only ever consulted once a question
  // already HAS an answer — an empty one always shows the list, since picking
  // a type is the only thing left to do.
  const [pickerOpen, setPickerOpen] = useState(false);
  const textInputRef = useRef(null);
  const errorRef = useRef(null);

  // The builder can be taller than the window once a table is open, so the
  // banner at the top may be off screen at the moment Save is clicked. Bring
  // it into view rather than leaving the click looking like it did nothing.
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  const inTableMode = !!table;

  const startTable = ({ rows, cols }) => {
    setAskingTableSize(false);
    setPieces([]);
    setTable(defaultTableState(rows, cols));
  };

  // A piece's default, cleaned to "undefined" (not set) rather than an empty
  // array/string — an all-unchecked checklist default is the same as no
  // default at all, not a default of "nothing". useCallback so the `draft`
  // preview below can depend on it without recomputing every render.
  const defaultValueFor = useCallback(
    (key) => {
      const v = compositePrefill[key];
      if (Array.isArray(v)) return v.length ? v : undefined;
      return v || undefined;
    },
    [compositePrefill]
  );

  // Text a piece still has sitting in its "Paste a list" box, keyed by piece
  // key. A REF, not state: typing there must cost no re-render and, above all,
  // must add no choice rows to the page — rows appearing while the pointer is
  // travelling to Save push the button out from under the click, and the save
  // silently doesn't happen. Save reads this instead, so one click both keeps
  // the pasted list and closes the editor. See OptionsEditor's onPendingChange.
  const pendingOptionsRef = useRef(new Map());

  // A reusable block is being dragged over this card. Lives here, not in
  // DropZone, because the whole card is the target for one.
  const [templateOver, setTemplateOver] = useState(false);

  // A piece's choices INCLUDING anything still staged in its paste box.
  const optionsOf = (p) => {
    const staged = parseOptionList(pendingOptionsRef.current.get(p.key));
    if (!staged.length) return p.options;
    return [...(p.options || []), ...staged];
  };

  // What `pieces` currently builds — 1 piece -> a plain question, 2+ -> a
  // composite. Used by the Save button.
  const piecesPayload = () => {
    if (pieces.length === 1) {
      const p = pieces[0];
      return {
        question: text.trim(),
        required,
        type: p.type,
        unit: p.unit,
        options: optionsOf(p),
        defaultValue: defaultValueFor(p.key),
      };
    }
    return {
      question: text.trim(),
      required,
      type: "Table",
      columns: pieces.map((p) => ({
        id: p.key,
        label: p.label || PIECE_TYPE_LABELS[p.type] || p.type,
        type: p.type,
        unit: p.unit,
        options: optionsOf(p),
      })),
      rowLabels: [],
      minRows: 1,
      maxRows: 1,
      prefillRows: [
        pieces.reduce((acc, p) => {
          if (compositePrefill[p.key]) acc[p.key] = compositePrefill[p.key];
          return acc;
        }, {}),
      ],
    };
  };

  const addPiece = (type) => {
    setError("");
    // Picking one closes the list again — it opened to answer a question that
    // has now been answered.
    setPickerOpen(false);
    if (type === "Table") {
      if (pieces.length > 0) {
        confirm(
          "Starting a table clears the answer pieces you've already added — a repeatable table is its own question, not something you mix with other pieces.",
          { title: "Switch to a table?", confirmLabel: "Switch to a table", tone: "danger" }
        ).then((ok) => {
          if (ok) setAskingTableSize(true);
        });
        return;
      }
      setAskingTableSize(true);
      return;
    }
    setPieces((prev) => [...prev, blankPiece(type)]);
  };

  const discardTable = () => {
    setTable(null);
  };

  const updatePiece = (key, next) =>
    setPieces((prev) => prev.map((p) => (p.key === key ? next : p)));
  const removePiece = (key) => setPieces((prev) => prev.filter((p) => p.key !== key));
  const movePiece = (key, dir) =>
    setPieces((prev) => {
      const i = prev.findIndex((p) => p.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  // Names for the questions a rule can point at, so the summary line can say
  // "Pregnant? = Yes" rather than a raw id. Both lists, because a rule may
  // reference a question outside this heading's scoped candidates.
  const labelById = useMemo(
    () =>
      new Map(
        [...branchAllCandidates, ...branchCandidates].map((c) => [c.id, c.question])
      ),
    [branchCandidates, branchAllCandidates]
  );

  // Whether this question currently has a visibility rule, and that rule in
  // one line. Read from the DRAFT rather than from `visibleWhen`: the draft is
  // what the modal is editing, and buildCondition() drops an unfinished rule,
  // so a half-set rule would otherwise flicker the button back to "Make
  // conditional" between picking a question and picking its answer.
  const hasCondition = branchDraft.complex || branchDraft.rules.length > 0;
  const conditionSummary = useMemo(() => {
    const cond = buildCondition(branchDraft.mode, branchDraft.rules);
    if (!cond) return branchDraft.complex ? "an advanced rule" : "";
    const described = describeCondition(cond, labelById);
    if (described.complex) return "an advanced rule";
    return described.parts.join(described.mode === "all" ? " and " : " or ");
  }, [branchDraft, labelById]);

  const save = () => {
    if (!text.trim()) {
      setError("Give the question some text before saving.");
      textInputRef.current?.focus();
      return;
    }
    if (inTableMode) {
      if (table.columns.length === 0) {
        setError("Add at least one column, or remove the table.");
        return;
      }
      onSave(tablePayload(table, text.trim(), required));
      return;
    }
    if (pieces.length === 0) {
      setError("Add at least one answer piece from the left, or click a type to add it.");
      return;
    }
    onSave(piecesPayload());
  };

  return (
    <div
      // The WHOLE card takes a reusable block, not just the answer box inside
      // it — hunting for one small target with a block already in hand is the
      // thing that made this feel unreliable. Anywhere on the question does it.
      //
      // Field types still land only on the answer box (DropZone): that drop
      // adds ONE answer to a specific question, so where it goes is the point.
      // A block replaces the whole question, so it has nowhere else to go.
      onDragOver={
        onDropTemplate
          ? (ev) => {
              if (!isTemplateDrag(ev)) return;
              ev.preventDefault();
              ev.dataTransfer.dropEffect = "copy";
              if (!templateOver) setTemplateOver(true);
            }
          : undefined
      }
      onDragLeave={
        onDropTemplate
          ? (ev) => {
              // dragleave bubbles from every child, so moving between them
              // would flicker this off. Only a leave that actually exits the
              // card counts.
              if (ev.currentTarget.contains(ev.relatedTarget)) return;
              setTemplateOver(false);
            }
          : undefined
      }
      onDrop={
        onDropTemplate
          ? (ev) => {
              if (!isTemplateDrag(ev)) return;
              ev.preventDefault();
              setTemplateOver(false);
              const templateId = readTemplateId(ev);
              if (templateId) onDropTemplate(templateId);
            }
          : undefined
      }
      className={`rounded-2xl border p-3.5 space-y-3 transition ${
        templateOver
          ? "border-2 border-dashed border-amber-300/80 bg-amber-400/[0.10]"
          : "border-purple-300/30 bg-purple-500/[0.06]"
      }`}
    >
      {/* What dropping will do, said before it happens — the block replaces
          this question rather than being added beside it. */}
      {templateOver && (
        <div className="flex items-center gap-1.5 rounded-xl border border-amber-300/40 bg-amber-400/15 px-2.5 py-1.5 text-[11px] font-semibold text-amber-100">
          <LibraryBig className="w-3.5 h-3.5 shrink-0" />
          Drop anywhere to use this standard block here — no heading, just the
          questions.
        </div>
      )}

      {/* At the TOP, not down by the Save button. Save is usually reached
          after scrolling a long way through a table, so an error printed
          beside it landed mid-screen — well away from the Question field it
          is nearly always about. */}
      {error && (
        <div
          role="alert"
          ref={errorRef}
          className="flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/15 px-3 py-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 text-rose-300 mt-0.5" />
          <p className="text-xs text-rose-100">{error}</p>
        </div>
      )}

      <div>
        <span className="text-[11px] uppercase tracking-wide text-slate-400">Question</span>
        <div className="mt-1 flex items-center gap-2">
          <input
            ref={textInputRef}
            autoFocus
            className={`${inputCls} flex-1`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              // Typing the missing question text clears the complaint about
              // it there and then, rather than on the next Save attempt.
              if (error) setError("");
            }}
            placeholder="What's the question? (e.g. Weight at screening)"
          />
          <label
            data-tour="builder-required"
            className="shrink-0 inline-flex items-center gap-1.5 cursor-pointer select-none px-2.5 h-[2.6rem] rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
          >
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-white/[0.04] accent-purple-500"
            />
            <span className="text-xs text-slate-300 whitespace-nowrap">Required</span>
          </label>
          {/* Up here beside Required, not a permanently-open rule builder at
              the bottom. Branching is the exception, not something every
              question needs answered — so it costs one button until you want
              it, and the button says which state it is in. */}
          {onVisibilityChange && (
            <button
              type="button"
              onClick={() => setCondOpen(true)}
              data-tour="builder-conditional"
              title={
                hasCondition
                  ? "Change when this question appears"
                  : "Only show this question after a certain answer"
              }
              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 h-[2.6rem] rounded-xl border text-xs whitespace-nowrap transition ${
                hasCondition
                  ? "border-fuchsia-400/40 bg-fuchsia-400/15 text-fuchsia-100 hover:bg-fuchsia-400/25"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]"
              }`}
            >
              <GitBranch className="w-3.5 h-3.5" />
              {hasCondition ? "Conditional" : "Make conditional"}
            </button>
          )}
          {/* Up here with the other per-question switches, not at the foot of
              the editor — it belongs to the question, not to the list of
              answers, and down there it was one more thing to scroll past.
              Hidden until there IS an answer: an empty question already shows
              the full type list, so there is nothing to reveal. */}
          {!inTableMode && pieces.length > 0 && !pickerOpen && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-tour="builder-add-answer"
              title="Add a second answer that prints under this same question"
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 h-[2.6rem] rounded-xl border border-white/10 bg-white/[0.03] text-xs text-slate-300 whitespace-nowrap hover:bg-white/[0.06] transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add another answer
            </button>
          )}
        </div>
        {/* The rule itself, stated in one line, so closing the modal doesn't
            mean losing sight of what was set. */}
        {hasCondition && conditionSummary && (
          <p className="mt-1.5 text-[11px] text-fuchsia-200/90">
            Only shows when {conditionSummary}
          </p>
        )}
      </div>

      {inTableMode ? (
        // The whole table — its columns, its rows, and the values that print
        // already filled in — is edited on the grid itself, so what the
        // coordinator is building is on screen the entire time.
        <TableGridEditor
          table={table}
          setTable={setTable}
          onRemove={discardTable}
          confirm={confirm}
        />
      ) : (
        // The drop target WRAPS the answers rather than sitting under them as
        // its own box, so dragging a type in from the left rail keeps working
        // without costing a permanent empty frame at the foot of the editor.
        <DropZone onDropType={addPiece} active={pieces.length > 0}>
          <div className="space-y-2">
            {pieces.length > 0 && (
              <span className="text-[11px] uppercase tracking-wide text-slate-400">
                {pieces.length === 1
                  ? "Answer"
                  : `${pieces.length} answers — these print together under one question`}
              </span>
            )}
            {pieces.map((p, i) => (
              <PieceCard
                key={p.key}
                piece={p}
                index={i}
                count={pieces.length}
                onChange={(next) => updatePiece(p.key, next)}
                onRemove={() => removePiece(p.key)}
                onMoveUp={() => movePiece(p.key, -1)}
                onMoveDown={() => movePiece(p.key, 1)}
                confirm={confirm}
                usedByOption={pieces.length === 1 ? usedByOption : undefined}
                defaultValue={compositePrefill[p.key]}
                onDefaultChange={(v) =>
                  setCompositePrefill((prev) => ({ ...prev, [p.key]: v }))
                }
                onPendingOptions={(textValue) =>
                  pendingOptionsRef.current.set(p.key, textValue)
                }
              />
            ))}

            {/* The type list appears only while a type is actually being
                CHOSEN — on an empty question, or after asking for another
                answer from the button up beside Required. Once a question has
                its answer, a permanent wall of twelve "+ Type" chips was the
                largest thing on screen and the answer to a question nobody was
                asking any more. */}
            {(pieces.length === 0 || pickerOpen) && (
              <div>
                <div className="flex items-center gap-1.5 mb-2 text-[11px] text-slate-400">
                  <Sparkles className="w-3.5 h-3.5 text-purple-300" />
                  {pieces.length === 0
                    ? onDropTemplate
                      ? "Pick what the answer looks like — or drag in an answer type, or a whole standard block, from the left."
                      : "Pick what the answer looks like — or drag one in from the left."
                    : "Add another answer — the two print together under one question."}
                  {pieces.length > 0 && (
                    // "Close", not "Cancel": the builder's own Cancel button
                    // (which abandons the whole question) is on screen at the
                    // same time, and two of them would be a genuinely risky
                    // thing to mistake for one another.
                    <button
                      type="button"
                      onClick={() => setPickerOpen(false)}
                      className="ml-auto text-slate-500 hover:text-white transition"
                    >
                      Close
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PIECE_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => addPiece(t)}
                      title={FIELD_TYPE_HINTS[t]}
                      className="px-2 py-1 rounded-lg text-[11px] text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] hover:text-white transition"
                    >
                      + {PIECE_TYPE_LABELS[t]}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPiece("Table")}
                    className="px-2 py-1 rounded-lg text-[11px] text-rose-200 bg-rose-400/10 border border-rose-400/25 hover:bg-rose-400/20 transition inline-flex items-center gap-1"
                  >
                    <Table2 className="w-3 h-3" /> Repeatable table…
                  </button>
                </div>
              </div>
            )}
          </div>
        </DropZone>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-slate-200 px-3 py-2 rounded-lg text-sm transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          className="inline-flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 text-white px-3 py-2 rounded-lg text-sm transition"
        >
          <Check className="w-4 h-4" /> {editing ? "Save" : "Add question"}
        </button>
      </div>

      {/* Step one of adding a table: how big? Cancelling leaves the question
          exactly as it was — no half-built table left behind. */}
      {askingTableSize && (
        <TableSizeModal
          onConfirm={startTable}
          onCancel={() => setAskingTableSize(false)}
        />
      )}

      {/* The rule builder, on demand. It persists on every click exactly as it
          did when it lived open at the bottom of the builder — "Done" just
          closes the panel, it is not a save step, so nothing is lost by
          closing it and nothing needs confirming. */}
      {condOpen && onVisibilityChange && (
        <ConditionModal
          onClose={() => setCondOpen(false)}
          hasCondition={hasCondition}
        >
          <ConditionBuilder
            mode={branchDraft.mode}
            rules={branchDraft.rules}
            complex={branchDraft.complex}
            candidates={branchCandidates}
            allCandidates={branchAllCandidates}
            sectionLabelOf={sectionLabelOf}
            currentSectionLabel={sectionLabelOf?.(initial?.id)}
            defaultQuestionId={defaultBranchQuestionId}
            onChange={(mode, rules) => {
              setBranchDraft({ mode, rules, complex: false });
              onVisibilityChange(buildCondition(mode, rules));
            }}
            onClear={() => {
              setBranchDraft({ mode: "any", rules: [], complex: false });
              onVisibilityChange(undefined);
            }}
          />
        </ConditionModal>
      )}
    </div>
  );
}
